import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import CustomTooltip from '../../../Tooltip';
import PreviewOriginal from '../../../PreviewOriginal';
import ConstructorLandings from '../../../constructors/Landings/ConstructorLandings.container';
import LandingPreview from '../../../LandingPreview';
import {getHostingType} from "../../../../helpers/common";
import CustomNotify from '../../../Notify';
import DataComponentPromo from "../../../DataComponent/DataComponentPromo";
import {cap} from "../../../../config";
import { I18nContext } from 'utils/I18n/context';

const columnTableData = {
    header: {
        0: {className: 'text-center', style: {width: 50}},
        1: {className: 'text-center'},
        2: {style: {width: 150}},
        3: {style: {width: 150}}
    },
    body: {
        0: {className: 'text-center'},
        1: {className: 'text-center'}
    }
};

const OfferPagePromoLandings = ({ 
    hostingTypes, 
    proportions, 
    headerNames, 
    landings, 
    loading, 
    totalPages, 
    getLandings: getLandingsAction, 
    saveLanding 
}) => {
    const { t } = useContext(I18nContext);
    const { id } = useParams();
    const [state, setState] = useState({
        offerId: id,
        landings: [],
        hostingTypes: hostingTypes,
        proportions: proportions,
        headerNames: headerNames,
        loading: loading,
        totalPages: totalPages,
        modalIsVisible: false,
        constructorIsVisible: false,
        selectedLandings: {},
    });

    const staticNames = t?.offersPage?.offer || { download: 'Download' };

    useEffect(() => {
        setState(prev => ({
            ...prev,
            offerId: id,
            loading: loading,
            landings: Array.isArray(landings) ? landings : [],
            hostingTypes: Array.isArray(hostingTypes) ? hostingTypes : [],
            totalPages: Number(totalPages) || 0
        }));
    }, [id, loading, landings, hostingTypes, totalPages]);

    const downloadZip = useCallback(async (event) => {
        let index = null;
        if(event.target.hasAttribute('data-index')) {
            index = event.target.getAttribute('data-index');
        } else if(event.target.closest('.banner-download').hasAttribute('data-index')) {
            index = event.target.closest('.banner-download').getAttribute('data-index');
        }

        if(null === index) {
            console.error('Error downloading ZIP, landing not found');
            CustomNotify('Download error, landing not found', 'error');
        }

        const landing = state.landings[index];
        const url = landing.is_external && landing.external_url
            ? getExternalIndexUrl(landing.external_url)
            : landing.url;

        try {
            const modifiedUrl = url.replace('/index.html', '.zip');
            const parts = url.split('/');
            const fileName = parts[parts.length - 2] + '.zip';

            const aTag = document.createElement("a");
            aTag.href = modifiedUrl;
            aTag.setAttribute("download", fileName);
            document.body.appendChild(aTag);
            aTag.click();
            aTag.remove();
            CustomNotify('Download started', 'success');
        } catch (error) {
            console.error('Error downloading ZIP:', error);
            CustomNotify('Download error', 'error');
        }
    }, [state.landings]);

    const showModalPreview = useCallback((data) => {
        setState(prev => ({
            ...prev,
            modalIsVisible: true,
            selectedLandings: data
        }));
    }, []);

    const showConstructor = useCallback((data) => {
        setState(prev => ({
            ...prev,
            constructorIsVisible: true,
            selectedLandings: data
        }));
    }, []);

    const closeModal = useCallback(() => {
        setState(prev => ({
            ...prev,
            modalIsVisible: false
        }));
    }, []);

    const closeConstructor = useCallback(() => {
        setState(prev => ({
            ...prev,
            constructorIsVisible: false
        }));
    }, []);

    const onClickShowModalPreview = useCallback((event) => {
        const index = event.target.closest('.control-item').getAttribute('data-index');
        showModalPreview(state.landings[index]);
    }, [state.landings, showModalPreview]);

    const onClickShowConstructor = useCallback((index) => {
        showConstructor(state.landings[index]);
    }, [state.landings, showConstructor]);

    const getExternalPreviewUrl = useCallback((url) => {
        return `${url}/preview.jpeg`;
    }, []);

    const getExternalIndexUrl = useCallback((url) => {
        return `${url}/index.html`;
    }, []);

    const imageExists = useCallback((url) => {
        var img = new Image();
        img.onload = function() { return true; };
        img.onerror = function() { return false; }
        img.src = url;
    }, []);

    const getLandings = useCallback(() => {
        let landings = [];

        state.landings.map((landing, i) => {
            const hostingType = getHostingType(landing.is_external);

            if(landing.params) {
                landing.params = !(landing.params instanceof Object) ? JSON.parse(landing.params) : landing.params;
            } else {
                landing.params = {};
            }

            const dataPreview = {
                url: landing.preview_dynamic_url,
                originalWidth: 1800,
                originalHeight: 1600
            };

            let itemTitle = landing.title.slice(0, 60);
            if (itemTitle.length < landing.title.length) {
                itemTitle += ' ...';
            }

            let imgURL = imageExists(landing.preview_dynamic_url) ? landing.preview_dynamic_url : cap;

            landings.push({
                id: landing.id,
                title: (
                    <CustomTooltip id={`banner-view-title-${i}`} tooltip={<PreviewOriginal {...dataPreview} />}
                                   placement={'right'} theme={'preview'}>
                        <div className="table-title" id={`banner-view-title-${i}`}>{itemTitle}</div>
                    </CustomTooltip>
                ),
                name: staticNames[hostingType],
                image: (
                    <div className="offer-item__table-img">
                        {landing.preview_dynamic_url
                            ? (
                                <CustomTooltip id={`link-preview-${i}`} tooltip={<PreviewOriginal {...dataPreview} />} placement={'right'} theme={'preview'}>
                                    <a href={landing.preview_dynamic_url} id={`link-preview-${i}`} target="_blank">
                                        <img src={imgURL} alt="" />
                                    </a>
                                </CustomTooltip>
                            )
                            : (
                                <a href={landing.preview_dynamic_url} target="_blank">
                                    <img src={cap} alt="" />
                                </a>
                            )
                        }
                    </div>
                ),
                action: (
                    <div className="offer-item__promo-banners-wrap">
                        <div className="offer-item__promo-banners-toolbar">
                            <a className="btn banner-download btn-white generate-link"
                               href='#'
                               data-index={i}
                               onClick={downloadZip}
                            >
                                {staticNames.download}
                            </a>
                        </div>
                    </div>
                )
            });
        });

        return landings;
    }, [state.landings, staticNames, imageExists, downloadZip]);

    const onDataTableAction = useCallback((dataComponentState, actionType) => {
        getLandingsAction({
            ...dataComponentState,
            offerId: state.offerId
        }, actionType);
    }, [getLandingsAction, state.offerId]);

    return (
        <React.Fragment>
            <DataComponentPromo
                onAction={onDataTableAction}
                totalPages={state.totalPages}
                headerNames={state.headerNames}
                data={getLandings()}
                loading={state.loading}
                withoutDatepicker={true}
                textInput={true}
                alignPagination={true}
                columnData={columnTableData}
                hidden={true}
            />
            <LandingPreview
                isVisible={state.modalIsVisible}
                url={state.selectedLandings.preview_dynamic_url}
                title={state.selectedLandings.title}
                width={state.selectedLandings.width}
                height={state.selectedLandings.height}
                onClose={closeModal}
            />
            <ConstructorLandings
                visible={state.constructorIsVisible}
                landing={state.selectedLandings}
                onClose={closeConstructor}
                onSave={saveLanding}
            />
        </React.Fragment>
    );
};

OfferPagePromoLandings.propTypes = {
    hostingTypes: PropTypes.array,
    proportions: PropTypes.array,
    headerNames: PropTypes.array,
    loading: PropTypes.bool,
    totalPages: PropTypes.number,
    getLandings: PropTypes.func,
    saveLanding: PropTypes.func
};

OfferPagePromoLandings.defaultProps = {
    hostingTypes: [],
    proportions: [],
    headerNames: [],
    loading: false,
    totalPages: 0,
    getLandings: () => {},
    saveLanding: () => {}
};

export { OfferPagePromoLandings };
